import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../services/api';

const getTriggerUpdatedAtMs = (trigger) => {
  if (!trigger?.updated_at) {
    return null;
  }

  const timestamp = Date.parse(trigger.updated_at);
  return Number.isFinite(timestamp) ? timestamp : null;
};

const isIncomingTriggerOlder = (incoming, existing) => {
  const incomingUpdatedAt = getTriggerUpdatedAtMs(incoming);
  const existingUpdatedAt = getTriggerUpdatedAtMs(existing);

  if (incomingUpdatedAt === null || existingUpdatedAt === null) {
    return false;
  }

  return incomingUpdatedAt < existingUpdatedAt;
};

const mergeTriggerData = (existing, incoming) => {
  if (!existing) {
    return incoming;
  }

  if (isIncomingTriggerOlder(incoming, existing)) {
    return existing;
  }

  return { ...existing, ...incoming };
};

const mergeTriggerList = (existingTriggers, incomingTriggers) => {
  if (!Array.isArray(incomingTriggers)) {
    return existingTriggers;
  }

  const existingById = new Map(existingTriggers.map(trigger => [trigger.uuid, trigger]));

  return incomingTriggers.map(trigger => (
    mergeTriggerData(existingById.get(trigger.uuid), trigger)
  ));
};

export const fetchTriggers = createAsyncThunk(
  'triggers/fetchTriggers',
  async (params = {}) => {
    const response = await api.get('/api/triggers/', { params });
    return response.data;
  }
);

export const fetchActiveTriggers = createAsyncThunk(
  'triggers/fetchActive',
  async () => {
    const response = await api.get('/api/triggers/active_triggers/');
    return response.data;
  }
);

export const createTrigger = createAsyncThunk(
  'triggers/create',
  async (triggerData) => {
    const response = await api.post('/api/triggers/', triggerData);
    return response.data;
  }
);

export const createTriggerGrid = createAsyncThunk(
  'triggers/createGrid',
  async (gridData) => {
    const response = await api.post('/api/triggers/bulk_grid/', gridData);
    return response.data;
  }
);

export const updateTrigger = createAsyncThunk(
  'triggers/update',
  async ({ id, data }) => {
    const response = await api.patch(`/api/triggers/${id}/`, data);
    return response.data;
  }
);

export const toggleTriggerStatus = createAsyncThunk(
  'triggers/toggleStatus',
  async (id) => {
    const response = await api.post(`/api/triggers/${id}/toggle_status/`);
    return response.data;
  }
);

export const deleteTrigger = createAsyncThunk(
  'triggers/delete',
  async (id) => {
    await api.delete(`/api/triggers/${id}/`);
    return id;
  }
);

const triggerSlice = createSlice({
  name: 'triggers',
  initialState: {
    triggers: [],
    activeTriggers: [],
    loading: false,
    error: null,
    currentFetchRequestId: null,
    latestFetchRequestId: null,
    filters: {
      status: null,
      trading_pair: null,
    },
  },
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    updateSingleTrigger: (state, action) => {
      // Update or add a single trigger in the list
      // MERGE instead of replace to preserve fields not in WebSocket updates (e.g., roi_info)
      const triggerData = action.payload;
      const index = state.triggers.findIndex(t => t.uuid === triggerData.uuid);
      if (index >= 0) {
        // Merge new data with existing trigger to preserve fields like roi_info
        state.triggers[index] = mergeTriggerData(state.triggers[index], triggerData);
      } else {
        state.triggers.push(triggerData);
      }
    },
    updateManyTriggers: (state, action) => {
      const byId = new Map(state.triggers.map((trigger, index) => [trigger.uuid, index]));

      action.payload.forEach((triggerData) => {
        if (!triggerData?.uuid) {
          return;
        }

        const index = byId.get(triggerData.uuid);
        if (index !== undefined) {
          state.triggers[index] = mergeTriggerData(state.triggers[index], triggerData);
        } else {
          byId.set(triggerData.uuid, state.triggers.length);
          state.triggers.push(triggerData);
        }
      });
    },
    removeSingleTrigger: (state, action) => {
      // Remove a trigger from the list
      const triggerId = action.payload;
      state.triggers = state.triggers.filter(t => t.uuid !== triggerId);
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch triggers
      .addCase(fetchTriggers.pending, (state, action) => {
        state.loading = true;
        state.currentFetchRequestId = action.meta.requestId;
        state.latestFetchRequestId = action.meta.requestId;
      })
      .addCase(fetchTriggers.fulfilled, (state, action) => {
        if (state.latestFetchRequestId !== action.meta.requestId) {
          return;
        }

        state.loading = false;
        state.currentFetchRequestId = null;
        state.triggers = mergeTriggerList(state.triggers, action.payload.results || action.payload);
      })
      .addCase(fetchTriggers.rejected, (state, action) => {
        if (state.latestFetchRequestId !== action.meta.requestId) {
          return;
        }

        state.loading = false;
        state.currentFetchRequestId = null;
        state.error = action.error.message;
      })
      // Fetch active triggers
      .addCase(fetchActiveTriggers.fulfilled, (state, action) => {
        state.activeTriggers = action.payload;
      })
      // Create trigger
      .addCase(createTrigger.fulfilled, (state, action) => {
        const index = state.triggers.findIndex(t => t.uuid === action.payload.uuid);
        if (index !== -1) {
          state.triggers[index] = mergeTriggerData(state.triggers[index], action.payload);
        } else {
          state.triggers.unshift(action.payload);
        }
      })
      // Update trigger
      .addCase(updateTrigger.fulfilled, (state, action) => {
        const index = state.triggers.findIndex(t => t.uuid === action.payload.uuid);
        if (index !== -1) {
          state.triggers[index] = mergeTriggerData(state.triggers[index], action.payload);
        } else {
          state.triggers.push(action.payload);
        }
      })
      // Toggle status
      .addCase(toggleTriggerStatus.fulfilled, (state, action) => {
        const index = state.triggers.findIndex(t => t.uuid === action.payload.uuid);
        if (index !== -1) {
          state.triggers[index] = mergeTriggerData(state.triggers[index], action.payload);
        } else {
          state.triggers.push(action.payload);
        }
      })
      // Delete trigger
      .addCase(deleteTrigger.fulfilled, (state, action) => {
        state.triggers = state.triggers.filter(t => t.uuid !== action.payload);
      });
  },
});

export const { setFilters, updateSingleTrigger, updateManyTriggers, removeSingleTrigger } = triggerSlice.actions;
export default triggerSlice.reducer;
